import getConfig from 'next/config';
import { DEFAULT_GO_API_HOST, envVarWithProtocolAndDefault } from 'server/constants';
import { getBaseApiUrl } from 'utils/url';
import { isModel } from 'utils/typeguards/cfEntities';
import { ApiMethodSignature } from 'types/swagger/clarifai-api';
import cogoToast from 'components/ToastMessage/ToastMessage';
import { modelSnippets } from './modelPredict';
import { workflowSnippets } from './workflowPredict';
import { llmSnippets } from './modelPredict/llm';
import { InputType, WorkflowSupportedInputTypes, ModelSnippetFn, ModelLanguage } from './types';
import { textToImageSnippets } from './modelPredict/textToImage';
import { visualDetectorSnippets } from './modelPredict/visualDetector';
import { visualSegmenterSnippets } from './modelPredict/visualSegmenter';
import { visualSegmenterSnippetsForWorkflow } from './workflowPredict/visualSegmenter';
import { imageToTextSnippets } from './modelPredict/imageToText';
import { generatePythonSDKForLLMWithMethodSignatures } from './modelPredict/pythonLlmMethodSignature';
import { generatePythonSDKForLLMOpenAI } from './modelPredict/pythonLlmOpenAI';
import { generatePythonSDKForLLMMcp } from './modelPredict/pythonLlmMcp';
import { generateNodeSDKForLLMOpenAI } from './modelPredict/nodeLlmOpenAI';
import { generateNodeSDKForLLMVercel } from './modelPredict/nodeLlmVercel';
import { visualClassifierSnippets } from './modelPredict/visualClassifier';

const { publicRuntimeConfig } = getConfig();
const apiHost = getBaseApiUrl(envVarWithProtocolAndDefault(publicRuntimeConfig.NEXT_PUBLIC_GO_API_HOST, DEFAULT_GO_API_HOST));

type Props = {
  language: ModelLanguage;
  personalAccessToken?: string;
  silent?: boolean;
  deploymentId?: string;
  method_signatures?: ApiMethodSignature[];
} & (
  | {
      entity: CF.API.Models.Model;
    }
  | {
      entity: CF.API.Workflows.Workflow;
      workflowInputType?: string;
    }
);

type WorkflowSupportedInputTypeValues = keyof typeof WorkflowSupportedInputTypes;

const inputTypeToInputUrl: Record<InputType, string> = {
  [InputType.audio]: 'https://samples.clarifai.com/negative_sentence_1.wav',
  [InputType.text]: 'https://samples.clarifai.com/negative_sentence_12.txt',
  [InputType.video]: 'https://samples.clarifai.com/beer.mp4',
  [InputType.image]: 'https://samples.clarifai.com/metro-north.jpg',
};

export function getCodeSnippet({ personalAccessToken = 'YOUR_PAT_HERE', silent, entity, ...props }: Props) {
  if (isModel(entity)) {
    const inputType = getModelInputType(entity);
    const modelSnippetFn = getModelSnippetFn(entity, props.language, props.method_signatures);

    if (!entity.model_version) {
      if (!silent) {
        cogoToast.info(`No model version found for model ${entity.id}`);
      }
    }
    return modelSnippetFn({
      appId: entity.app_id ?? '',
      userId: entity.user_id ?? '',
      modelId: entity.id,
      modelVersionId: entity.model_version?.id ?? 'No version id available',
      pat: personalAccessToken,
      inputUrl: inputTypeToInputUrl[inputType],
      apiHost,
      deploymentId: props.deploymentId,
      method_signatures: props.method_signatures,
    });
  }

  const inputType = 'workflowInputType' in props && checkSupportedWorkflowInputType(props.workflowInputType);

  // Remove the language check when working on: https://clarifai.atlassian.net/browse/MRK-2932
  if (
    !inputType ||
    props.language === ModelLanguage.PHP ||
    props.language === ModelLanguage.Javascript ||
    props.language === ModelLanguage.PythonOpenAI ||
    props.language === ModelLanguage.PythonMCP ||
    props.language === ModelLanguage.NodeJSOpenAI ||
    props.language === ModelLanguage.NodeJSVercel
  ) {
    return '';
  }

  const workflowSnippetFn = getWorkflowSnippetFn(entity, props.language);

  if (workflowSnippetFn) {
    return workflowSnippetFn({
      appId: entity.app_id,
      userId: entity.user_id,
      workflowId: entity.id,
      pat: personalAccessToken,
      inputUrl: inputTypeToInputUrl[inputType],
      apiHost,
    });
  }

  return workflowSnippets[props.language][inputType]({
    workflowId: entity.id,
    userId: entity.user_id,
    appId: entity.app_id,
    pat: personalAccessToken,
    inputUrl: inputTypeToInputUrl[inputType],
    apiHost,
  });
}

function getModelSnippetFn(model: CF.API.Models.Model, language: ModelLanguage, methodSignatures?: ApiMethodSignature[]): ModelSnippetFn {
  if (language === 'Python (OpenAI)') {
    return generatePythonSDKForLLMOpenAI;
  } else if (language === 'Python (MCP)') {
    return generatePythonSDKForLLMMcp;
  } else if (language === 'NodeJS (OpenAI)') {
    return generateNodeSDKForLLMOpenAI;
  } else if (language === 'NodeJS (Vercel AI SDK)') {
    return generateNodeSDKForLLMVercel;
  } else if (model.model_type_id === 'visual-segmenter') {
    return visualSegmenterSnippets[language];
  } else if (model.model_type_id === 'visual-detector') {
    return visualDetectorSnippets[language];
  } else if (model.model_type_id === 'text-to-image') {
    return textToImageSnippets[language];
  } else if (language === 'Python (SDK)' && methodSignatures) {
    return generatePythonSDKForLLMWithMethodSignatures;
  } else if (model.use_cases?.includes('llm')) {
    return llmSnippets[language];
  } else if (model.model_type_id === 'visual-classifier' && Object.keys(imageToTextSnippets).includes(language)) {
    return visualClassifierSnippets[language as unknown as keyof typeof imageToTextSnippets];
  } else if (model.model_type_id === 'image-to-text' && Object.keys(imageToTextSnippets).includes(language)) {
    return imageToTextSnippets[language as unknown as keyof typeof imageToTextSnippets];
  }

  const inputType = getModelInputType(model);
  return modelSnippets[language][inputType];
}

function getWorkflowSnippetFn(workflow: CF.API.Workflows.Workflow, language: ModelLanguage) {
  const workflowModelTypes = getWorkflowModelType(workflow);
  const visualSegmenterLanguage = Object.keys(visualSegmenterSnippetsForWorkflow) as unknown as keyof typeof visualSegmenterSnippetsForWorkflow;
  const languageSupported = visualSegmenterLanguage.includes(language);
  if (workflowModelTypes.includes('visual-segmenter') && languageSupported) {
    return visualSegmenterSnippetsForWorkflow[language as typeof visualSegmenterLanguage];
  }
  return null;
}

function getModelInputType(model: CF.API.Models.Model): InputType {
  const fields_map = model.model_version?.input_info?.fields_map;

  if (fields_map?.audio) return InputType.audio;
  if (fields_map?.text || model.model_type_id === 'text-to-text') return InputType.text;

  // fallback
  return InputType.image;
}

function checkSupportedWorkflowInputType(workflowInputType?: string) {
  if (workflowInputType === 'any') {
    return InputType.image as WorkflowSupportedInputTypeValues;
  }

  return Boolean(workflowInputType && workflowInputType in WorkflowSupportedInputTypes) && (workflowInputType as WorkflowSupportedInputTypeValues);
}

function getWorkflowModelType(workflow: CF.API.Workflows.Workflow): string[] {
  return (
    workflow.nodes?.map((node) => {
      return node.model?.model_type_id;
    }) ?? []
  );
}
